import type { ComponentAssociation } from "building-types/components/componentAssociationTypes";
import type { Doc } from "building-types/doc";
import type {
  ComponentAssociatedEntityHandle,
  ComponentAssociatedEntityType,
  DocEntityHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import objectFilter from "common/utils/objectFilter";
import uid from "common/utils/uid";
import type { DocEntityReference } from "../entity/docEntityReferenceTypes";

const EMPTY_COMPONENT_ASSOCIATIONS: readonly ComponentAssociation[] = Object.freeze([]);

/**
 * Secondary indexes over `doc.componentAssociations` for O(1) membership checks and
 * O(k) member/container listing (k = matching rows), instead of scanning the full table.
 *
 * Cached by table identity via {@link componentAssociationsIndexCache}. Docs share the
 * frozen empty table from `defaultDoc`, so empty indexes are shared correctly. When
 * associations change, a new table object is allocated and the cache misses naturally.
 */
export interface ComponentAssociationsIndex {
  /** entityType → entityId → associations for that member */
  readonly byMember: ReadonlyMap<ComponentAssociatedEntityType, ReadonlyMap<string, readonly ComponentAssociation[]>>;
  /** entityType → entityId → componentId → association */
  readonly byMemberAndComponent: ReadonlyMap<
    ComponentAssociatedEntityType,
    ReadonlyMap<string, ReadonlyMap<string, ComponentAssociation>>
  >;
  /** componentId → associations for that container */
  readonly byComponentId: ReadonlyMap<string, readonly ComponentAssociation[]>;
}

const componentAssociationsIndexCache = new WeakMap<Table<ComponentAssociation>, ComponentAssociationsIndex>();

function buildComponentAssociationsIndex(table: Table<ComponentAssociation>): ComponentAssociationsIndex {
  const byMember = new Map<ComponentAssociatedEntityType, Map<string, ComponentAssociation[]>>();
  const byMemberAndComponent = new Map<ComponentAssociatedEntityType, Map<string, Map<string, ComponentAssociation>>>();
  const byComponentId = new Map<string, ComponentAssociation[]>();

  for (const id in table) {
    const association = table[id];

    let byEntityId = byMember.get(association.entityType);
    if (!byEntityId) {
      byEntityId = new Map();
      byMember.set(association.entityType, byEntityId);
    }
    let memberAssociations = byEntityId.get(association.entityId);
    if (!memberAssociations) {
      memberAssociations = [];
      byEntityId.set(association.entityId, memberAssociations);
    }
    memberAssociations.push(association);

    let byEntityIdAndComponent = byMemberAndComponent.get(association.entityType);
    if (!byEntityIdAndComponent) {
      byEntityIdAndComponent = new Map();
      byMemberAndComponent.set(association.entityType, byEntityIdAndComponent);
    }
    let byComponent = byEntityIdAndComponent.get(association.entityId);
    if (!byComponent) {
      byComponent = new Map();
      byEntityIdAndComponent.set(association.entityId, byComponent);
    }
    byComponent.set(association.componentId, association);

    let containerAssociations = byComponentId.get(association.componentId);
    if (!containerAssociations) {
      containerAssociations = [];
      byComponentId.set(association.componentId, containerAssociations);
    }
    containerAssociations.push(association);
  }

  for (const byEntityId of byMember.values()) {
    for (const [entityId, associations] of byEntityId) {
      byEntityId.set(entityId, Object.freeze(associations) as ComponentAssociation[]);
    }
  }
  for (const byEntityIdAndComponent of byMemberAndComponent.values()) {
    for (const [entityId, byComponent] of byEntityIdAndComponent) {
      byEntityIdAndComponent.set(entityId, Object.freeze(byComponent) as Map<string, ComponentAssociation>);
    }
  }
  for (const [componentId, associations] of byComponentId) {
    byComponentId.set(componentId, Object.freeze(associations) as ComponentAssociation[]);
  }

  return { byMember, byMemberAndComponent, byComponentId };
}

/**
 * Returns a cached secondary index for `doc.componentAssociations`.
 * Safe to call repeatedly for the same table object during a doc-ref delta.
 */
export function getComponentAssociationsIndex(doc: Pick<Doc, "componentAssociations">): ComponentAssociationsIndex {
  const table = doc.componentAssociations;
  const cached = componentAssociationsIndexCache.get(table);
  if (cached) {
    return cached;
  }

  const index = buildComponentAssociationsIndex(table);
  componentAssociationsIndexCache.set(table, index);
  return index;
}

export function docAssociatedEntityExistsInDoc(
  doc: Pick<Doc, "optionSets">,
  entityType: ComponentAssociatedEntityType,
  entityId: ForeignEntityId<ComponentAssociatedEntityType>,
): boolean {
  switch (entityType) {
    case "OPTION_SET":
      return doc.optionSets[entityId] !== undefined;
    default:
      assertUnreachable(entityType);
  }
}

export function docListComponentAssociationsForEntity(
  doc: Pick<Doc, "componentAssociations">,
  entityHandle: ComponentAssociatedEntityHandle,
): readonly ComponentAssociation[] {
  const associations = getComponentAssociationsIndex(doc).byMember.get(entityHandle.type)?.get(entityHandle.id);
  return associations ?? EMPTY_COMPONENT_ASSOCIATIONS;
}

export function docGetComponentIdsForAssociatedEntity(
  doc: Pick<Doc, "componentAssociations">,
  entityHandle: ComponentAssociatedEntityHandle,
): string[] {
  const associations = docListComponentAssociationsForEntity(doc, entityHandle);
  if (associations.length === 0) {
    return [];
  }

  const componentIds = new Set<string>();
  for (const association of associations) {
    componentIds.add(association.componentId);
  }
  return Array.from(componentIds);
}

export function docGetAssociatedEntityIdsForComponent(
  doc: Pick<Doc, "componentAssociations" | "optionSets">,
  componentId: string,
  entityType: ComponentAssociatedEntityType,
): string[] {
  const associations = getComponentAssociationsIndex(doc).byComponentId.get(componentId);
  if (!associations || associations.length === 0) {
    return [];
  }

  const entityIds = new Set<string>();
  for (const association of associations) {
    if (
      association.entityType === entityType &&
      docAssociatedEntityExistsInDoc(doc, entityType, association.entityId)
    ) {
      entityIds.add(association.entityId);
    }
  }
  return Array.from(entityIds);
}

export function docGetComponentAssociation(
  doc: Pick<Doc, "componentAssociations">,
  entityHandle: ComponentAssociatedEntityHandle,
  componentId: string,
): ComponentAssociation | undefined {
  return getComponentAssociationsIndex(doc)
    .byMemberAndComponent.get(entityHandle.type)
    ?.get(entityHandle.id)
    ?.get(componentId);
}

export function docHasComponentAssociation(
  doc: Pick<Doc, "componentAssociations">,
  entityHandle: ComponentAssociatedEntityHandle,
  componentId: string,
): boolean {
  return docGetComponentAssociation(doc, entityHandle, componentId) !== undefined;
}

// Remove all component associations for a component.
export function docCleanupComponentAssociationsForComponent(doc: Doc, componentId: string): Doc {
  return {
    ...doc,
    componentAssociations: objectFilter(
      doc.componentAssociations,
      (association) => association.componentId !== componentId,
    ),
  };
}

// Remove all component associations for an entity.
export function docCleanupComponentAssociationsForEntity(
  doc: Doc,
  entityType: ComponentAssociatedEntityType,
  entityId: ForeignEntityId<ComponentAssociatedEntityType>,
): Doc {
  return {
    ...doc,
    componentAssociations: objectFilter(
      doc.componentAssociations,
      (association) => association.entityType !== entityType || association.entityId !== entityId,
    ),
  };
}

export function docCleanupComponentAssociationsForHandle(doc: Doc, handle: DocEntityHandle): Doc {
  switch (handle.type) {
    case "COMPONENT":
      return docCleanupComponentAssociationsForComponent(doc, handle.id);
    case "OPTION_SET":
      return docCleanupComponentAssociationsForEntity(doc, "OPTION_SET", handle.id);
    default:
      return doc;
  }
}

export function componentAssociationNew(
  entityHandle: ComponentAssociatedEntityHandle,
  componentId: ForeignEntityId<"COMPONENT">,
  sortIndex = 0,
): ComponentAssociation {
  return {
    id: uid(),
    entityType: entityHandle.type,
    entityId: entityHandle.id,
    componentId,
    sortIndex,
  };
}

/** Forward COMPONENT references from componentAssociations for doc-ref dependency traversal. */
export function extractDocComponentAssociationReferences(
  doc: Pick<Doc, "componentAssociations">,
  entityHandle: ComponentAssociatedEntityHandle,
): DocEntityReference[] {
  const associations = docListComponentAssociationsForEntity(doc, entityHandle);
  if (associations.length === 0) {
    return [];
  }

  const references: DocEntityReference[] = [];
  for (const association of associations) {
    references.push({
      fieldPath: "componentAssociations",
      referencedEntityType: "COMPONENT",
      referencedEntityId: association.componentId,
      entityType: entityHandle.type,
      entityId: entityHandle.id,
    });
  }
  return references;
}
